import PropTypes from 'prop-types';
import { MagnifyingGlass, ShieldWarning } from '@phosphor-icons/react';
import Spinner from '../components/atoms/Spinner/Spinner';
import ErrorAndRetry from '../components/atoms/ErrorAndRetry/ErrorAndRetry';
import NoDataFound from '../components/atoms/NoDataFound/NoDataFound';

/**
 * A simplified version of LoaderWrapper that takes explicit props for loading, error, and data states
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isLoading - Whether data is currently loading
 * @param {boolean} props.isError - Whether an error occurred
 * @param {boolean} props.isUnauthorized - Whether the user is unauthorized (403)
 * @param {boolean} props.isNotFound - Whether the resource was not found (404)
 * @param {Function} props.onRetry - Function to call when retry is clicked
 * @param {React.ReactNode} props.children - Content to render when data is loaded
 * @param {any} props.data - Data to check if empty
 * @param {string} props.noDataMessage - Message to display when no data is found
 * @param {boolean} props.isEmpty - Optional explicit check for empty state, defaults to checking if data is empty
 * @param {string} props.errorMessage - Custom error message
 * @param {string} props.unauthorizedMessage - Custom unauthorized message
 * @param {string} props.notFoundMessage - Custom not-found (404) message
 * @param {boolean} props.centerLoader - When true and loading, fill the parent (use with relative + min-h-full on the parent) and center the spinner like modal error states
 * @returns {React.ReactNode} The appropriate UI based on the current state
 */
const LoaderWrapperSimple = ({
  isLoading,
  isError,
  isUnauthorized = false,
  isNotFound = false,
  onRetry,
  children,
  data,
  noDataMessage = 'No data found',
  isEmpty = false,
  errorMessage = 'We encountered an issue while loading the data. This may be due to a temporary network problem or an issue at our end.',
  unauthorizedMessage = 'You do not have permission to view this content. Please contact your administrator if you believe this is an error.',
  notFoundMessage = 'The requested resource could not be found.',
  centerLoader = false,
}) => {
  // Check if data is empty if isEmpty is not explicitly provided
  const isDataEmpty =
    isEmpty || !data || (Array.isArray(data) && data.length === 0);

  // Show spinner when loading
  if (isLoading) {
    if (centerLoader) {
      return (
        <div className="absolute inset-0 flex items-center justify-center">
          <Spinner />
        </div>
      );
    }
    return <Spinner />;
  }

  // Show unauthorized state (403) with a polite message
  if (isUnauthorized) {
    return (
      <div className="absolute flex w-full h-full justify-center items-center top-0 bottom-0 left-0 right-0">
        <div className="flex flex-col items-center gap-4 max-w-md text-center px-4">
          <ShieldWarning className="text-amber-500 w-16 h-16" weight="thin" />
          <h3 className="text-lg font-semibold text-gray-700">
            Access Restricted
          </h3>
          <p className="text-gray-500 text-sm">{unauthorizedMessage}</p>
        </div>
      </div>
    );
  }

  // Not found (404)
  if (isNotFound) {
    return (
      <div className="absolute flex w-full h-full justify-center items-center top-0 bottom-0 left-0 right-0">
        <div className="flex flex-col items-center gap-4 max-w-md text-center px-4">
          <MagnifyingGlass className="text-slate-400 w-16 h-16" weight="thin" />
          <h3 className="text-lg font-semibold text-gray-700">Not Found</h3>
          <p className="text-gray-500 text-sm">{notFoundMessage}</p>
        </div>
      </div>
    );
  }

  // Show error state when there's an error
  if (isError) {
    return <ErrorAndRetry message={errorMessage} handleRetry={onRetry} />;
  }

  // Show no data message when data is empty
  if (isDataEmpty) {
    return <NoDataFound message={noDataMessage} />;
  }

  // Show children when data is loaded and not empty
  return <>{children}</>;
};

LoaderWrapperSimple.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  isError: PropTypes.bool,
  isUnauthorized: PropTypes.bool,
  isNotFound: PropTypes.bool,
  onRetry: PropTypes.func,
  children: PropTypes.node,
  data: PropTypes.any,
  noDataMessage: PropTypes.string,
  isEmpty: PropTypes.bool,
  errorMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  unauthorizedMessage: PropTypes.string,
  notFoundMessage: PropTypes.string,
  centerLoader: PropTypes.bool,
};

export default LoaderWrapperSimple;
